import React, { useCallback, useEffect, useState } from 'react';
import { Badge, Button, Col, Row } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { changesApi } from '../../../services/changesApi';
import type {
  ChangeAgendaQueuedSendSummaryItem,
  ChangeAgendaSendKind,
  ChangeAgendaSendSummaryItem,
  ChangeCalendarItem,
} from '../../../types/changes';
import { shortDispatchBadge } from '../agendaEmailDispatchStatus';
import { labelChangeStatus } from '../changeLabels';
import { formatChangePublicId } from '../changePublicId';
import { formatGoLiveWindow, formatGovernanceDate, formatGovernanceWindow } from '../goLiveWindow';
import CalendarAgendaEmailModal from './CalendarAgendaEmailModal';
import CalendarChangeAgendaPanel from './CalendarChangeAgendaPanel';

const BADGE_LABELS: Record<ChangeAgendaSendKind, string> = {
  committee: 'Riscos enviado',
  camud: 'CAMUD enviado',
};

interface Props {
  item: ChangeCalendarItem;
  onClear: () => void;
  canSend?: boolean;
  windowStart: string;
  windowEnd: string;
  categoryId?: number;
  project?: string;
  ownerId?: number;
  onSent?: (message: string) => void;
}

const CalendarSelectedChangeSummary: React.FC<Props> = ({
  item,
  onClear,
  canSend = false,
  windowStart,
  windowEnd,
  categoryId,
  project,
  ownerId,
  onSent,
}) => {
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [sentItems, setSentItems] = useState<ChangeAgendaSendSummaryItem[]>([]);
  const [queuedItems, setQueuedItems] = useState<ChangeAgendaQueuedSendSummaryItem[]>([]);

  const loadSummary = useCallback(async () => {
    try {
      const response = await changesApi.agendaSendSummary(item.id);
      setSentItems(response.items || []);
      setQueuedItems(response.queued_items || []);
    } catch {
      setSentItems([]);
      setQueuedItems([]);
    }
  }, [item.id]);

  useEffect(() => {
    setEmailModalOpen(false);
    void loadSummary();
  }, [item.id, loadSummary]);

  useEffect(() => {
    if (emailModalOpen) void loadSummary();
  }, [emailModalOpen, loadSummary]);

  const sentByKind = sentItems.reduce<Partial<Record<ChangeAgendaSendKind, ChangeAgendaSendSummaryItem>>>(
    (acc, entry) => {
      acc[entry.agenda_kind] = entry;
      return acc;
    },
    {},
  );
  const dispatchBadge = shortDispatchBadge({ sentItems, queuedItems });

  return (
    <>
      <div className="border rounded px-3 py-2 mb-2" style={{ background: '#fff8e6', borderColor: '#fd7e14' }}>
        <div className="d-flex justify-content-between align-items-start gap-2 mb-2">
          <div>
            <div className="fw-semibold">
              {formatChangePublicId(item.id)} · {item.title} · {labelChangeStatus(item.status)}
            </div>
            <div className="small text-muted">
              Datas reais da mudança. A grade só mostra o pedaço desta semana ou deste mês.
            </div>
            {sentItems.length || queuedItems.length ? (
              <div className="d-flex flex-wrap gap-1 mt-1">
                {sentItems.map((entry) => (
                  <Badge key={entry.agenda_kind} bg="success" className="fw-normal">
                    {BADGE_LABELS[entry.agenda_kind] || entry.agenda_kind_label}
                  </Badge>
                ))}
                {queuedItems.map((entry) => (
                  <Badge key={`q-${entry.queued_id}`} bg="warning" text="dark" className="fw-normal">
                    {entry.agenda_kind_label} agendado
                  </Badge>
                ))}
                {dispatchBadge ? (
                  <Badge bg="secondary" className="fw-normal">
                    {dispatchBadge}
                  </Badge>
                ) : null}
              </div>
            ) : null}
          </div>
          <div className="d-flex gap-2 flex-shrink-0">
            {canSend ? (
              <Button size="sm" variant="outline-secondary" onClick={() => setEmailModalOpen(true)}>
                Enviar e-mail
              </Button>
            ) : null}
            <Link to={`/changes/${item.id}`} className="btn btn-sm btn-outline-secondary">
              Abrir mudança
            </Link>
            <Button size="sm" variant="outline-secondary" onClick={onClear}>
              Limpar
            </Button>
          </div>
        </div>
        <Row className="g-2 small">
          <Col xs={12} md={4}>
            <div className="text-muted">Go Live</div>
            <div className="fw-semibold">{formatGoLiveWindow(item.go_live_start_at, item.go_live_end_at)}</div>
          </Col>
          <Col xs={12} md={4}>
            <div className="text-muted">Riscos e Impactos</div>
            <div className="fw-semibold">
              {formatGovernanceWindow(item.risk_committee_start_at, item.risk_committee_end_at)}
            </div>
          </Col>
          <Col xs={12} md={4}>
            <div className="text-muted">CAMUD</div>
            <div className="fw-semibold">{formatGovernanceDate(item.camud_go_no_go_date)}</div>
          </Col>
        </Row>
      </div>

      {canSend ? (
        <CalendarAgendaEmailModal
          show={emailModalOpen}
          onHide={() => setEmailModalOpen(false)}
          title={`E-mail · ${formatChangePublicId(item.id)}`}
          subtitle={item.title}
        >
          <CalendarChangeAgendaPanel
            embedded={false}
            changeId={item.id}
            changeTitle={item.title}
            windowStart={windowStart}
            windowEnd={windowEnd}
            categoryId={categoryId}
            project={project}
            ownerId={ownerId}
            sentByKind={sentByKind}
            queuedItems={queuedItems}
            sentItems={sentItems}
            onSent={(message) => onSent?.(message)}
            onSentRefresh={() => void loadSummary()}
            onComplete={() => setEmailModalOpen(false)}
          />
        </CalendarAgendaEmailModal>
      ) : null}
    </>
  );
};

export default CalendarSelectedChangeSummary;
